import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingOutlined } from "@ant-design/icons";
import { message, Modal, Spin } from "antd";
import axiosConfig from "../../config/axiosConfig";
import { AuthContext } from "../../context/authContext";
import { useWaba } from "../../context/wabaContext";
import PricingCalculator from "../dashboard/components/pages/PricingCalculator";

import "./PricingPage.css";

// ── Hardcoded fallback plans (used only if API is unavailable) ──
const FALLBACK_PLANS = [
  {
    planid: 'fallback-starter',
    plan_name: 'starter',
    display_name: 'Starter',
    monthly_price: '999.00',
    annual_price: '9990.00',
    credits: 1000,
    features: ['1,000 messages/month', 'Basic templates', 'Email support'],
    description: 'Best for small businesses',
    sort_order: 1,
  },
  {
    planid: 'fallback-professional',
    plan_name: 'professional',
    display_name: 'Professional',
    monthly_price: '2499.00',
    annual_price: '24990.00',
    credits: 5000,
    features: ['5,000 messages/month', 'Advanced templates', 'Priority support'],
    description: 'Ideal for growing businesses',
    sort_order: 2,
  },
  {
    planid: 'fallback-enterprise',
    plan_name: 'enterprise',
    display_name: 'Enterprise',
    monthly_price: '4999.00',
    annual_price: '49990.00',
    credits: 15000,
    features: ['15,000 messages/month', 'Custom templates', 'Dedicated support'],
    description: 'For large-scale operations',
    sort_order: 3,
  },
];

const PricingPage = () => {
  const [activePricing, setActivePricing] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const navigate = useNavigate();
  const [pricingCalcVisible, setPricingCalcVisible] = useState(false);
  const { currentUser } = useContext(AuthContext);
  
  // Try to use WABA context (available when inside dashboard)
  // Falls back to local state when outside dashboard (public pricing page)
  const wabaContext = useWaba();
  const [localHasWabaDetails, setLocalHasWabaDetails] = useState(true);
  const [localCheckingWaba, setLocalCheckingWaba] = useState(true);
  const [localWabaFetchError, setLocalWabaFetchError] = useState(false);
  
  // Use context values if available (inside dashboard), otherwise use local state
  const hasWabaDetails = wabaContext?.wabaDetails !== null ? wabaContext.hasWabaDetails : localHasWabaDetails;
  const checkingWaba = wabaContext?.wabaDetails !== null ? wabaContext.loadingWabaDetails : localCheckingWaba;
  const wabaFetchError = wabaContext?.wabaDetails !== null ? wabaContext.wabaFetchError : localWabaFetchError;

  // ── Fetch plans from API ──
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setPlansLoading(true);
        // Try public endpoint first (works for all users, returns active-only plans)
        const res = await axiosConfig.get('/admin/platform/public-plans');
        if (res.data?.entity?.plans && res.data.entity.plans.length > 0) {
          const sorted = [...res.data.entity.plans].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
          setPlans(sorted);
        } else {
          console.warn('Public plans API returned empty, using fallback');
          setPlans(FALLBACK_PLANS);
        }
      } catch (err) {
        console.warn('Public plans API not available, using fallback:', err.message);
        setPlans(FALLBACK_PLANS);
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, []);

  useEffect(() => {
    // Check if user is logged in
    const user = sessionStorage.getItem('user') || localStorage.getItem('user');
    if (user) {
      try {
        setUserInfo(JSON.parse(user));
        if (!wabaContext?.wabaDetails && wabaContext?.wabaDetails !== null) {
          // Context is available but wabaDetails is null - waiting for context to load
        } else if (wabaContext?.hasWabaDetails === undefined) {
          checkWabaDetailsLocally();
        }
      } catch (error) {
        console.error('Error parsing user info:', error);
      }
    } else if (currentUser) {
      setUserInfo(currentUser);
      if (wabaContext?.hasWabaDetails === undefined) {
        checkWabaDetailsLocally();
      }
    } else {
      setLocalCheckingWaba(false);
    }
  }, [wabaContext?.hasWabaDetails, wabaContext?.wabaDetails, currentUser]);

  const checkWabaDetailsLocally = async () => {
    try {
      setLocalCheckingWaba(true);
      setLocalWabaFetchError(false);
      const response = await axiosConfig.get('/embeddedsignup/customer-waba-details');
      
      if (response.data.success && response.data.data) {
        setLocalHasWabaDetails(true);
      } else {
        setLocalHasWabaDetails(false);
      }
    } catch (error) {
      console.error('Error checking WABA details:', error);
      const statusCode = error?.response?.status;
      if (statusCode === 500 || statusCode === 429 || !error?.response) {
        setLocalWabaFetchError(true);
      } else {
        setLocalHasWabaDetails(false);
        setLocalWabaFetchError(false);
      }
    } finally {
      setLocalCheckingWaba(false);
    }
  };

  const handlePlanSelection = async (plan) => {
    const user = sessionStorage.getItem('user') || localStorage.getItem('user');
    const isAuthenticated = user || currentUser;
    
    if (!isAuthenticated) {
      Modal.confirm({
        title: 'Login Required',
        content: 'You need to login to purchase a plan. Would you like to go to the login page?',
        onOk: () => navigate('/login'),
        onCancel: () => {},
      });
      return;
    }

    if (!userInfo) {
      message.error('User information not found. Please login again.');
      navigate('/login');
      return;
    }

    if (!hasWabaDetails && !wabaFetchError) {
      return;
    }

    const displayName = plan.display_name || plan.plan_name;
    setProcessingPlan(displayName);
    setLoading(true);

    try {
      const amount = activePricing === "monthly"
        ? Number(plan.monthly_price)
        : Number(plan.annual_price);
      const billingCycle = activePricing === "monthly" ? "monthly" : "annual";
      const description = `${displayName} Plan - ${billingCycle === "monthly" ? "Monthly" : "Annual"} Subscription`;
      
      // Step 1: Create Razorpay order (with plan info for auto-credit on verify)
      const orderResponse = await axiosConfig.post('/payment/order/create', {
        amount: amount,
        planid: plan.planid,
        billing_cycle: billingCycle,
        description: description
      });

      if (!orderResponse.data.status || typeof orderResponse.data.statuscode !== 'object' || !orderResponse.data.statuscode) {
        const serverMsg = orderResponse.data?.message || 'Failed to create order';
        throw new Error(serverMsg);
      }

      const orderData = orderResponse.data.statuscode;

      // Extract credit_info from response (if available)
      const creditInfo = orderResponse.data.entity?.credit_info || orderResponse.data.statuscode?.credit_info;

      // Step 1.5: Show credit preview confirmation before opening Razorpay
      const userConfirmed = await new Promise((resolve) => {
        Modal.confirm({
          title: 'Confirm Payment',
          icon: null,
          content: (
            <div style={{ padding: '8px 0' }}>
              <div style={{ background: '#f0f5ff', borderRadius: 10, padding: '16px 20px', marginBottom: 12, border: '1px solid #d6e4ff' }}>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#1d39c4' }}>
                  {displayName} Plan — {billingCycle === 'monthly' ? 'Monthly' : 'Annual'}
                </p>
              </div>
              {creditInfo ? (
                <div style={{ background: '#f6ffed', borderRadius: 10, padding: '16px 20px', border: '1px solid #b7eb8f' }}>
                  <p style={{ margin: '0 0 6px', fontSize: 14, color: '#389e0d', fontWeight: 600 }}>
                    💰 Pay ₹{Number(creditInfo.amount_paid).toLocaleString('en-IN')} → Get {Number(creditInfo.credits).toLocaleString('en-IN')} credits
                  </p>
                  <p style={{ margin: 0, fontSize: 12, color: '#52c41a' }}>
                    Rate: {Number(creditInfo.credits_per_rupee).toLocaleString('en-IN')} credits per ₹1
                  </p>
                </div>
              ) : (
                <p style={{ margin: 0, fontSize: 14, color: '#595959' }}>
                  Amount: <strong>₹{amount.toLocaleString('en-IN')}</strong>
                </p>
              )}
            </div>
          ),
          okText: 'Proceed to Pay',
          cancelText: 'Cancel',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });

      if (!userConfirmed) {
        setLoading(false);
        setProcessingPlan(null);
        message.info('Payment cancelled');
        return;
      }

      // Step 2: Open Razorpay checkout
      const options = {
        key: orderData.key_id,
        amount: orderData.razorpay_order.amount,
        currency: orderData.razorpay_order.currency,
        name: 'MessageOS',
        description: description,
        order_id: orderData.razorpay_order.id,
        handler: async function (response) {
          try {
            // Step 3: Verify payment
            const verifyResponse = await axiosConfig.post('/payment/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            // Debug: log full verify response to understand the shape
            console.log('🔍 Verify response full:', JSON.stringify(verifyResponse.data, null, 2));

            const rd = verifyResponse.data;
            const verifyData = rd?.entity || rd?.statuscode || rd?.data || rd;

            const isVerified =
              verifyData?.payment_verified === true ||
              rd?.status === true ||
              rd?.success === true;

            if (isVerified) {
              const planPurchase = verifyData.plan_purchase;
              const planName = planPurchase?.plan_name || displayName;
              const purchasedCycle = planPurchase?.billing_cycle || billingCycle;

              // Plan quota info from backend response
              const emailLimit = planPurchase?.email_limit || plan.email_limit || 0;
              const waCampaignLimit = planPurchase?.whatsapp_campaign_limit || plan.whatsapp_campaign_limit || 0;
              const isUnlimitedWa = planPurchase?.is_unlimited_whatsapp || plan.is_unlimited_whatsapp || false;

              setLoading(false);
              setProcessingPlan(null);

              Modal.success({
                title: 'Payment Successful!',
                content: (
                  <div>
                    <p>Your <strong>{planName}</strong> plan ({purchasedCycle}) is now active!</p>
                    {emailLimit > 0 && (
                      <p>📧 <strong>{Number(emailLimit).toLocaleString('en-IN')}</strong> emails per billing cycle</p>
                    )}
                    {isUnlimitedWa ? (
                      <p>💬 <strong>Unlimited</strong> WhatsApp campaigns</p>
                    ) : waCampaignLimit > 0 ? (
                      <p>💬 <strong>{Number(waCampaignLimit).toLocaleString('en-IN')}</strong> WhatsApp campaigns per billing cycle</p>
                    ) : null}
                    {planPurchase?.purchase?.expires_at && (
                      <p style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                        Valid until: {new Date(planPurchase.purchase.expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                ),
                onOk: () => navigate('/dashboard/pricing'),
              });
            } else {
              console.error('❌ Verification check failed. Full response:', rd);
              throw new Error('Payment verification failed!');
            }
          } catch (verifyError) {
            console.error('Payment verification error:', verifyError);
            message.error('Payment verification failed. Please contact support.');
            setLoading(false);
            setProcessingPlan(null);
          }
        },
        prefill: {
          name: userInfo.name || userInfo.companyName,
          email: userInfo.email,
          contact: userInfo.phone
        },
        theme: {
          color: '#1890ff'
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
            setProcessingPlan(null);
            message.info('Payment cancelled');
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (error) {
      console.error('Payment error:', error);
      message.error(`Payment failed: ${error.message}`);
      setLoading(false);
      setProcessingPlan(null);
    }
  };

  const isMonthly = activePricing === "monthly";

  // Mark the second plan as "popular" (Professional)
  const pricingPlans = plans.map((plan, idx) => ({
    ...plan,
    popular: idx === 1, // second plan = popular
  }));

  return (
    <div className="pricing-page">
      <section className="pp-section">
        <div className="pp-container">

          {/* Header */}
          <div className="pp-header">
            <h2>Simple, Transparent Pricing</h2>
            <p>Choose the plan that works for your business.</p>
          </div>

          {/* Info Boxes */}
          <div className="pp-info-boxes">
            <div className="pp-info-box">
              <span className="pp-info-icon">🏷️</span>
              <div className="pp-info-content">
                <h4>Platform Fee</h4>
                <p>Monthly subscription for MessageOS features, team access, automation, and support.</p>
              </div>
            </div>
            <span className="pp-info-plus">+</span>
            <div className="pp-info-box">
              <span className="pp-info-icon">💬</span>
              <div className="pp-info-content">
                <h4>WhatsApp Conversation Charges</h4>
                <p>Pay-as-you-go charges by WhatsApp. User-initiated: ₹0.50 &nbsp;|&nbsp; Business-initiated: ₹1.50</p>
              </div>
            </div>
          </div>

          {/* WABA Setup Notice */}
          {userInfo && !checkingWaba && !hasWabaDetails && !wabaFetchError && (
            <div className="pp-waba-notice">
              <span>ℹ&nbsp;&nbsp;Complete WhatsApp Business setup to purchase credits</span>
              <button className="pp-waba-link" onClick={() => navigate('/whatsappregistration')}>
                Set up now →
              </button>
            </div>
          )}

          {/* ═══════════ SUBSCRIPTION PLANS TAB ═══════════ */}
              {/* Toggle */}
              <div className="pp-toggle-bar">
                <span className={isMonthly ? "pp-toggle-label active" : "pp-toggle-label"}>Monthly</span>
                <button
                  className={`pp-switch ${!isMonthly ? "annual" : ""}`}
                  onClick={() => setActivePricing(isMonthly ? "yearly" : "monthly")}
                  aria-label="Toggle billing period"
                >
                  <span className="pp-switch-thumb" />
                </button>
                <span className={!isMonthly ? "pp-toggle-label active" : "pp-toggle-label"}>
                  Annual <span className="pp-save-badge">Save 17%</span>
                </span>
              </div>

              {/* Loading state */}
              {plansLoading && (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                  <Spin size="large" />
                  <p style={{ marginTop: 16, color: '#666' }}>Loading plans...</p>

                </div>
              )}
              {/* Cards Grid */}
              {!plansLoading && (
                <div className="pp-cards">
                  {pricingPlans.map((plan) => {
                    const displayName = plan.display_name || plan.plan_name || 'Plan';
                    const monthlyPrice = Number(plan.monthly_price || 0);
                    const annualPrice = Number(plan.annual_price || 0);
                    const features = plan.features || [];
                    const isDisabled = (loading && processingPlan === displayName) ||
                      (userInfo && !checkingWaba && !hasWabaDetails && !wabaFetchError);

                    // Validity info
                    const validityDays = isMonthly
                      ? (plan.monthly_validity_days || 30)
                      : (plan.annual_validity_days || 365);

                    return (
                      <div key={plan.planid} className={`pp-card ${plan.popular ? "popular" : ""}`}>
                        {plan.popular && (
                          <div className="pp-popular-badge">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 4 }}>
                              <path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7L12 16.4 5.7 21l2.3-7L2 9.4h7.6z" />
                            </svg>
                            Popular
                          </div>
                        )}

                        <p className="pp-plan-name">{displayName}</p>

                        <div className="pp-amount">
                          <span className="pp-currency">₹</span>
                          <span className="pp-number">
                            {isMonthly
                              ? monthlyPrice.toLocaleString('en-IN')
                              : annualPrice.toLocaleString('en-IN')}
                          </span>
                          <span className="pp-period">/ {isMonthly ? "mo" : "yr"}</span>
                        </div>

                        <p className="pp-billed">{isMonthly ? "billed monthly" : "billed annually"}</p>

                        {/* Validity badge */}
                        <p className="pp-validity-badge">
                          📅 Valid for {validityDays} days
                        </p>

                        {/* Plan Quotas */}
                        {(plan.email_limit > 0 || plan.whatsapp_campaign_limit > 0 || plan.is_unlimited_whatsapp) && (
                          <div style={{ textAlign: 'center', margin: '4px 0 8px', padding: '8px 12px', background: '#f0f5ff', borderRadius: 8, border: '1px solid #d6e4ff' }}>
                            {plan.email_limit > 0 && (
                              <p style={{ color: '#1890ff', fontWeight: 600, fontSize: 13, margin: '2px 0' }}>
                                📧 {Number(plan.email_limit).toLocaleString('en-IN')} emails / cycle
                              </p>
                            )}
                            {plan.is_unlimited_whatsapp ? (
                              <p style={{ color: '#25D366', fontWeight: 600, fontSize: 13, margin: '2px 0' }}>
                                💬 Unlimited WhatsApp campaigns
                              </p>
                            ) : plan.whatsapp_campaign_limit > 0 ? (
                              <p style={{ color: '#25D366', fontWeight: 600, fontSize: 13, margin: '2px 0' }}>
                                💬 {Number(plan.whatsapp_campaign_limit).toLocaleString('en-IN')} WA campaigns / cycle
                              </p>
                            ) : null}
                          </div>
                        )}

                        {plan.credits > 0 && (
                          <p style={{ textAlign: 'center', color: '#999', fontWeight: 500, fontSize: 12, margin: '2px 0 8px', textDecoration: 'line-through' }}>
                            Legacy: +{Number(plan.credits).toLocaleString('en-IN')} credits
                          </p>
                        )}

                        <ul className="pp-features">
                          {features.map((feature, idx) => (
                            <li key={idx}>
                              <svg className="pp-check" width="16" height="16" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <div className="pp-divider" />

                        <button
                          className={`pp-cta ${plan.popular ? "primary" : "outline"}`}
                          onClick={() => handlePlanSelection(plan)}
                          disabled={isDisabled}
                        >
                          {loading && processingPlan === displayName ? (
                            <><LoadingOutlined style={{ marginRight: 8 }} />Processing...</>
                          ) : (userInfo && !checkingWaba && !hasWabaDetails && !wabaFetchError) ? (
                            "Set up WhatsApp first"
                          ) : (
                            `Choose ${displayName}`
                          )}
                        </button>

                        <p className="pp-card-desc">{plan.description}</p>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* WhatsApp Pricing Calculator */}
              <div style={{ textAlign: 'center', marginTop: 32 }}>
                <button
                  onClick={() => setPricingCalcVisible(true)}
                  style={{
                    background: 'linear-gradient(135deg, #25D366, #128C7E)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 9999,
                    padding: '12px 32px',
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'opacity 0.2s',
                    boxShadow: '0 2px 8px rgba(37,211,102,0.25)',
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  💬 WhatsApp Pricing Calculator
                </button>
                <p style={{ fontSize: 13, color: '#65676b', marginTop: 8 }}>
                  Check Meta's official per-message rates by country
                </p>
              </div>

        </div>
      </section>

      <PricingCalculator visible={pricingCalcVisible} onClose={() => setPricingCalcVisible(false)} />
    </div>
  );
};  

export default PricingPage;